import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  MessageSquare,
  Bot,
  User,
  Send,
  Phone,
  Sparkles,
  Calendar,
  Scissors,
  MapPin,
  Pizza,
  CupSoda,
  Package,
  CreditCard,
  Clock,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { TypingIndicator } from '../ui/typing-indicator';
import { fadeInUp, fadeInLeft, messageReveal } from '../../lib/motion';
import { useWhatsAppDemo } from '../../hooks/useWhatsAppDemo';

interface MessageItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

interface Message {
  type: 'user' | 'bot';
  text: string;
  time: string;
  items?: MessageItem[];
}

interface ChatScenario {
  title: string;
  messages: Message[];
}

// Static demo data — hoisted to module scope so its reference is stable across
// renders (previously it was recreated each render, which retriggered the autoplay
// effect and tore down/rebuilt the interval on every state change).
const CHAT_SCENARIOS: ChatScenario[] = [
  {
    title: 'Prenotazione Appuntamento',
    messages: [
      { type: 'user', text: 'Ciao, vorrei prenotare un taglio di capelli', time: '14:30' },
      { type: 'bot', text: 'Ciao! Sarò felice di aiutarti con la prenotazione. Per quando vorresti l\'appuntamento?', time: '14:30' },
      { type: 'user', text: 'Domani alle 15:00 se possibile', time: '14:31' },
      { type: 'bot', text: 'Perfetto! Ho trovato disponibilità domani alle 15:00. Confermo l\'appuntamento per il taglio di capelli?', time: '14:31' },
      { type: 'user', text: 'Sì, confermo', time: '14:32' },
      {
        type: 'bot',
        text: 'Prenotazione confermata! Ti invierò un promemoria 1 ora prima. A presto!',
        time: '14:32',
        items: [
          { icon: Calendar, label: 'Domani 15:00' },
          { icon: Scissors, label: 'Taglio capelli' },
          { icon: MapPin, label: 'Via Roma 123' },
        ],
      },
    ],
  },
  {
    title: 'Ordine E-commerce',
    messages: [
      { type: 'user', text: 'Vorrei ordinare 2 pizze margherita', time: '19:15' },
      { type: 'bot', text: 'Ottima scelta!\n\n2x Pizza Margherita - €16.00\n\nVuoi aggiungere altro al tuo ordine?', time: '19:15' },
      { type: 'user', text: 'Sì, anche una coca cola', time: '19:16' },
      {
        type: 'bot',
        text: 'Perfetto! Il tuo ordine:\n\nTotale: €19.00\n\nConfermi l\'ordine? Consegna stimata: 30-40 min',
        time: '19:16',
        items: [
          { icon: Pizza, label: '2x Pizza Margherita - €16.00' },
          { icon: CupSoda, label: '1x Coca Cola - €3.00' },
        ],
      },
      { type: 'user', text: 'Confermo', time: '19:17' },
      {
        type: 'bot',
        text: 'Ordine confermato! Ti terrò aggiornato sullo stato della preparazione!',
        time: '19:17',
        items: [
          { icon: Package, label: 'Numero ordine: #12345' },
          { icon: CreditCard, label: 'Pagamento alla consegna' },
          { icon: Clock, label: 'Tempo stimato: 35 minuti' },
        ],
      },
    ],
  },
  {
    title: 'Supporto Clienti',
    messages: [
      { type: 'user', text: 'Ho un problema con il mio ordine', time: '10:30' },
      { type: 'bot', text: 'Mi dispiace per l\'inconveniente! Sono qui per aiutarti. Puoi condividere il numero del tuo ordine?', time: '10:30' },
      { type: 'user', text: 'Ordine #98765', time: '10:31' },
      { type: 'bot', text: 'Ho trovato il tuo ordine. Vedo che è stato spedito ieri. Qual è il problema specifico che stai riscontrando?', time: '10:31' },
      { type: 'user', text: 'Non è ancora arrivato', time: '10:32' },
      { type: 'bot', text: 'Ho controllato il tracking: il pacco è in consegna oggi entro le 18:00. Ti invio subito il link di tracciamento e un buono sconto del 10% per l\'attesa. Va bene?', time: '10:32' },
    ],
  },
];

const DemoInteractive: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const requestDemo = useWhatsAppDemo();

  const currentMessages = CHAT_SCENARIOS[selectedScenario].messages;

  useEffect(() => {
    const messages = CHAT_SCENARIOS[selectedScenario].messages;
    const timer = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < messages.length - 1) {
          if (messages[prev + 1].type === 'bot') {
            setShowTyping(true);
            setTimeout(() => {
              setShowTyping(false);
            }, 1500);
          }
          return prev + 1;
        } else {
          setTimeout(() => {
            setCurrentStep(0);
          }, 3000);
          return prev;
        }
      });
    }, 2500);

    return () => clearInterval(timer);
  }, [selectedScenario]);

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [currentStep, showTyping]);

  const handleScenarioChange = (index: number) => {
    setSelectedScenario(index);
    setCurrentStep(0);
    setShowTyping(false);
    // Reset scroll to top when changing scenario
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = 0;
    }
  };

  return (
    <section id="demo" className="py-16 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <motion.div {...fadeInUp} className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-text-primary mb-6">
            Vedi <span className="text-primary">ChatLy</span> in Azione
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Guarda come ChatLy automatizza conversazioni reali per il tuo business con l'intelligenza artificiale
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">
            <motion.div {...fadeInLeft}>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Scegli uno scenario:</h3>
                <div className="flex flex-wrap gap-3">
                  {CHAT_SCENARIOS.map((scenario, index) => (
                    <Button
                      key={scenario.title}
                      variant={selectedScenario === index ? 'default' : 'outline'}
                      onClick={() => handleScenarioChange(index)}
                      className="text-sm"
                    >
                      {scenario.title}
                    </Button>
                  ))}
                </div>
              </div>

              <Card className="overflow-hidden rounded-3xl border-border shadow-sm">
                <div className="bg-primary text-white p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-primary" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-bold">ChatLy Bot</h3>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full" aria-hidden="true"></div>
                      <span className="text-xs opacity-90">Online</span>
                    </div>
                  </div>
                </div>

                <CardContent className="p-0">
                  <div
                    ref={chatContainerRef}
                    className="h-96 overflow-y-auto bg-muted/40 p-4 space-y-4"
                  >
                    <AnimatePresence>
                      {currentMessages.slice(0, currentStep + 1).map((message, index) => (
                        <motion.div
                          key={`${selectedScenario}-${index}`}
                          variants={messageReveal}
                          initial="hidden"
                          animate="visible"
                          className={`flex ${message.type === 'user' ? 'justify-start' : 'justify-end'}`}
                        >
                          <div className={`max-w-xs lg:max-w-sm p-3 rounded-2xl shadow-sm ${
                            message.type === 'user'
                              ? 'bg-card text-text-primary border border-border'
                              : 'bg-primary text-white'
                          }`}>
                            <div className="flex items-start gap-2">
                              {message.type === 'user' ? (
                                <User className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                              ) : (
                                <Bot className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                              )}
                              <div className="flex-1">
                                <p className="text-sm whitespace-pre-line">{message.text}</p>
                                {message.items && (
                                  <ul className="mt-2 flex flex-col gap-1.5">
                                    {message.items.map((item) => {
                                      const Icon = item.icon;
                                      return (
                                        <li
                                          key={item.label}
                                          className="flex items-center gap-2 rounded-full bg-white/15 px-2.5 py-1 text-xs"
                                        >
                                          <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                                          <span>{item.label}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                                <span className={`text-xs mt-1 block ${
                                  message.type === 'user' ? 'text-text-secondary' : 'text-white/75'
                                }`}>
                                  {message.time}
                                </span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}

                      {showTyping && (
                        <motion.div
                          variants={messageReveal}
                          initial="hidden"
                          animate="visible"
                          exit={{ opacity: 0 }}
                          className="flex justify-end"
                        >
                          <div className="bg-primary text-white p-3 rounded-2xl shadow-sm">
                            <div className="flex items-center gap-2">
                              <Bot className="h-4 w-4" aria-hidden="true" />
                              <TypingIndicator />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="bg-card border-t border-border p-4 flex items-center gap-3">
                    <label htmlFor="demo-chat-input" className="sr-only">
                      Scrivi un messaggio (anteprima demo, disabilitata)
                    </label>
                    <input
                      id="demo-chat-input"
                      type="text"
                      placeholder="Scrivi un messaggio..."
                      disabled
                      className="flex-1 p-3 bg-muted rounded-full text-sm text-text-secondary"
                    />
                    <Button size="icon" disabled className="rounded-full" aria-label="Invia messaggio">
                      <Send className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="lg:col-span-4">
            <Card className="sticky top-8 rounded-3xl bg-card border-border shadow-sm">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-text-primary mb-4">
                  Perché ChatLy Funziona
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Bot className="h-5 w-5 text-primary" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-text-primary">AI Contestuale</h4>
                      <p className="text-sm text-text-secondary">Comprende il contesto e mantiene conversazioni naturali</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-primary" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-text-primary">Integrazione Completa</h4>
                      <p className="text-sm text-text-secondary">Si connette ai tuoi sistemi esistenti automaticamente</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="h-5 w-5 text-primary" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-text-primary">Risposta Immediata</h4>
                      <p className="text-sm text-text-secondary">I clienti ricevono risposte istantanee 24/7</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={requestDemo}
                  className="w-full mt-6 gap-2"
                  variant="gradient"
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                  Richiedi Demo Personalizzata
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoInteractive;
